import { CSSProperties, ElementType, Fragment, ReactNode, useEffect, useState } from 'react';

import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Header, Table, flexRender } from '@tanstack/react-table';
import styled from 'styled-components';

import { IconChevronDownSmall } from '@components/icons/chevron-down-small';
import { IconFilter } from '@components/icons/filter';
import { IconMoveEnd } from '@components/icons/move-end';
import { IconMoveStart } from '@components/icons/move-start';
import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { Menu } from '@components/menu';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionSlowest, transitionStandard } from '@utils/transitions';

import { FilterConfig } from './filters/common';
import { FILTER_POPOVER_MAX_WIDTH } from './filters/constants';
import { FilterContent } from './filters/filter-content';
import { FilterTarget, getFilterTargetForColumn } from './filters/get-filter-target-for-column';
import { rowHeight } from './renderers';
import { Resizer } from './resizer';
import { useSortLocale } from './sorters';
import { FilterActions, TableStore } from './table-store';
import { stickyEndClassName } from './table-utils';
import { AvailableColumn, ColumnType, DataItemType } from './types';
import { getColumnWidth, getIsTableAction } from './use-column-sizing-vars';
import { DragBorder, HeaderContent } from './use-reorder-dnd';

type UseFixedHeaderContentParams<DataItem extends DataItemType> = {
  table: Table<DataItem>;
  store: TableStore;
  availableColumns: Array<AvailableColumn<DataItem>>;
  activeColumnTypeMap: Record<string, string>;
  fixedColumnIndex: number;
  fixedEndColumnsLength: number;
  additionalHeaderMenuItems?: (shortId: string) => ReactNode;
  filterConfig?: FilterConfig;
  filterActions: FilterActions;
  translations: {
    hideField: string;
    moveToStart: string;
    moveToEnd: string;
    filterByThisField: string;
  };
};

export const useFixedHeaderContent = <DataItem extends DataItemType>({
  table,
  store,
  availableColumns,
  activeColumnTypeMap,
  fixedColumnIndex,
  fixedEndColumnsLength,
  additionalHeaderMenuItems,
  filterConfig,
  filterActions,
  translations,
}: UseFixedHeaderContentParams<DataItem>) => {
  const sortTranslations = useSortLocale();
  const { useColumnOrder, useColumnVisibility } = store;
  const { columnOrder, setColumnOrder } = useColumnOrder();
  const { columnVisibility, setColumnVisibility } = useColumnVisibility();
  const isOnlyVisibleColumn = Object.values(columnVisibility).filter(Boolean).length === 1;

  return () =>
    table.getHeaderGroups().map((headerGroup) => (
      <tr key={headerGroup.id}>
        <TableSpacer tag="th" />
        <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
          {headerGroup.headers.map((header, headerIndex) => {
            const headerSort = header.column.getIsSorted();
            const isSortable = header.column.getCanSort();
            const actual = availableColumns.find((c) => c.id === header.column.id);
            const sorterStrings =
              actual?.sortText ??
              sortTranslations[activeColumnTypeMap[header.id] as ColumnType] ??
              sortTranslations.text;
            const isFixedColumn = headerIndex === fixedColumnIndex;
            const isLastDataColumn =
              headerIndex === headerGroup.headers.length - 1 - fixedEndColumnsLength;

            const filterTarget = filterConfig
              ? getFilterTargetForColumn(header.id, filterConfig)
              : null;

            return (
              <HeaderColumn
                key={header.column.id}
                store={store}
                header={header}
                isFixedColumn={isFixedColumn}
              >
                <Menu
                  placement="bottom"
                  width={FILTER_POPOVER_MAX_WIDTH}
                  buttonProps={{
                    style: menuStyleOverride,
                    variant: 'ghost-no-background',
                    children: (
                      <HeaderContent>
                        <Text color="textSecondary" font="semibold14">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Text>
                        <IconChevronDownSmall color="elementPurple50" data-slot="chevron" />
                      </HeaderContent>
                    ),
                  }}
                >
                  {({ close }) => (
                    <ColumnHeaderDropdownContent
                      close={close}
                      menuHeader={actual?.menuHeader?.(close)}
                      menuFooter={actual?.menuFooter}
                      additionalItems={additionalHeaderMenuItems?.(header.id)}
                      isSortable={isSortable}
                      headerSort={headerSort}
                      sorterStrings={sorterStrings}
                      isFixedColumn={isFixedColumn}
                      isLastDataColumn={isLastDataColumn}
                      isOnlyVisibleColumn={isOnlyVisibleColumn}
                      onSortAsc={() => header.column.toggleSorting(false)}
                      onSortDesc={() => header.column.toggleSorting(true)}
                      onMoveToStart={() => {
                        const newOrder = columnOrder.filter((id) => id !== header.id);
                        newOrder.unshift(header.id);
                        setColumnOrder(newOrder);
                      }}
                      onMoveToEnd={() => {
                        const newOrder = columnOrder.filter((id) => id !== header.id);
                        newOrder.push(header.id);
                        setColumnOrder(newOrder);
                      }}
                      onHide={() =>
                        setColumnVisibility({ ...columnVisibility, [header.id]: false })
                      }
                      filterConfig={filterConfig}
                      filterTarget={filterTarget}
                      filterActions={filterActions}
                      columnTitle={actual?.title}
                      translations={translations}
                    />
                  )}
                </Menu>
              </HeaderColumn>
            );
          })}
        </SortableContext>
        <TableSpacer tag="th" />
        <TableSpacer tag="th" fill />
      </tr>
    ));
};

const HeaderColumn = <DataType extends DataItemType>({
  children,
  header,
  isFixedColumn,
  store,
}: {
  children: ReactNode;
  header: Header<DataType, unknown>;
  isFixedColumn: boolean;
  store: TableStore;
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } = useSortable({
    id: header.column.id,
    disabled: getIsTableAction(header.id),
    data: { header },
  });
  const width = getColumnWidth(header.id);
  const className = isFixedColumn ? stickyEndClassName : undefined;
  const isTableAction = getIsTableAction(header.id);

  return (
    <th
      className={className}
      id={`${header.id}-th`}
      key={header.id}
      style={{ transform: CSS.Translate.toString(transform), width }}
      ref={isTableAction ? undefined : setNodeRef}
    >
      {isTableAction ? (
        flexRender(header.column.columnDef.header, header.getContext())
      ) : (
        <HeaderContainer style={{ opacity: isDragging ? 0.5 : 1 }}>
          <DraggableHeaderContainer {...attributes} {...listeners}>
            {children}
          </DraggableHeaderContainer>
          {!isTableAction && <Resizer header={header} store={store} />}
          {isDragging && <DragBorder />}
        </HeaderContainer>
      )}
    </th>
  );
};

type SorterStrings = { asc: string; desc: string };

type ColumnHeaderDropdownContentProps = {
  close: () => void;
  menuHeader?: ReactNode;
  menuFooter?: ReactNode;
  additionalItems?: ReactNode;
  isSortable: boolean;
  headerSort: 'asc' | 'desc' | false;
  sorterStrings: SorterStrings;
  isFixedColumn: boolean;
  isLastDataColumn: boolean;
  isOnlyVisibleColumn: boolean;
  onSortAsc: () => void;
  onSortDesc: () => void;
  onMoveToStart: () => void;
  onMoveToEnd: () => void;
  onHide: () => void;
  filterConfig?: FilterConfig;
  filterTarget: FilterTarget | null;
  filterActions: FilterActions;
  columnTitle?: string;
  translations: {
    hideField: string;
    moveToStart: string;
    moveToEnd: string;
    filterByThisField: string;
  };
};

const ColumnHeaderDropdownContent = ({
  close,
  menuHeader,
  menuFooter,
  additionalItems,
  isSortable,
  headerSort,
  sorterStrings,
  isFixedColumn,
  isLastDataColumn,
  isOnlyVisibleColumn,
  onSortAsc,
  onSortDesc,
  onMoveToStart,
  onMoveToEnd,
  onHide,
  filterConfig,
  filterTarget,
  filterActions,
  columnTitle,
  translations,
}: ColumnHeaderDropdownContentProps) => {
  const [view, setView] = useState<'menu' | 'filter'>('menu');

  useEffect(() => {
    if (view !== 'filter') return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [view, close]);

  if (view === 'filter' && filterConfig && filterTarget) {
    return (
      <FilterContent
        config={filterConfig}
        actions={{
          ...filterActions,
          addFilter: (data) => {
            filterActions.addFilter(data);
            filterActions.expandFilters();
          },
        }}
        initialData={filterTarget}
        closeMenu={close}
        hideNavigation
        onBack={() => setView('menu')}
        backLabel={columnTitle}
      />
    );
  }

  return (
    <Fragment>
      {menuHeader}
      {isSortable && (
        <Fragment>
          <Menu.Item
            label={sorterStrings.asc}
            icon={<IconSortAsc />}
            isDisabled={headerSort === 'asc'}
            onSelect={onSortAsc}
          />
          <Menu.Item
            label={sorterStrings.desc}
            icon={<IconSortDesc />}
            isDisabled={headerSort === 'desc'}
            onSelect={onSortDesc}
          />
          <Menu.Separator />
        </Fragment>
      )}
      <Menu.Item
        label={translations.moveToStart}
        icon={<IconMoveStart />}
        isDisabled={isFixedColumn}
        onSelect={onMoveToStart}
      />
      <Menu.Item
        label={translations.moveToEnd}
        icon={<IconMoveEnd />}
        isDisabled={isLastDataColumn}
        onSelect={onMoveToEnd}
      />
      <Menu.Separator />
      {filterTarget && (
        <Menu.Item
          label={translations.filterByThisField}
          icon={<IconFilter />}
          selectionType="open"
          onSelect={() => setView('filter')}
        />
      )}
      <Menu.Item
        label={translations.hideField}
        isDisabled={isOnlyVisibleColumn}
        icon={<IconVisibilityHidden />}
        onSelect={onHide}
      />
      {additionalItems}
      {menuFooter}
    </Fragment>
  );
};

export const TableSpacer = ({
  tag: Tag = 'td',
  fill = false,
}: {
  tag?: ElementType;
  fill?: boolean;
}) => {
  return (
    <Tag className={fill ? 'table-space-fill' : 'table-spacer'}>
      <div />
    </Tag>
  );
};

const menuStyleOverride: CSSProperties = {
  width: '100%',
  height: '100%',
  justifyContent: 'flex-start',
  padding: 0,
  transform: 'none',
};

const DraggableHeaderContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '> div': {
    height: '100%',
  },
});

const HeaderContainer = styled.div({
  position: 'relative',
  display: 'flex',
  width: '100%',
  height: rowHeight,
  transition: transitionSlowest('background-color'),
  padding: `0 ${pxToRem(8)}`,

  [`${HeaderContent} [data-slot="chevron"]`]: {
    display: 'none',
    transition: transitionStandard('all'),
  },

  '[data-slot="drag-handle"]': {
    display: 'none',
  },

  '&:hover': {
    backgroundColor: theme.color.uiTrans10,
    [`${HeaderContent} [data-slot="chevron"]`]: {
      display: 'block',
    },

    '[data-slot="drag-handle"]': {
      display: 'flex',
    },
  },

  '> div:first-child': {
    width: '100%',
    height: '100%',
  },
});
